import delay from '@redux-saga/delay-p';
import { isNearlyExpired, reauthDelay } from 'affiny-core/helpers/auth';
import { isWeb } from 'affiny-core/helpers/targetCheck/targetCheck';
import { NO_REFRESH_TOKEN } from 'affiny-core/modules/api/core/middlewares/middlewaresHelpers';
import { handleAsyncStatus } from 'affiny-core/modules/asynchronousState/saga';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { DangerousStore } from 'affiny-core/modules/dangerousStore';
import {
  hasPicturesFilled,
  selectAccountAnnounce,
  selectAccountAttributeElement,
} from 'affiny-core/redux/selectors/accountSelectors';
import { selectAccessToken } from 'affiny-core/redux/selectors/authSelectors';
import { getFirstUnansweredQuestion } from 'affiny-core/redux/selectors/memberChoicesSelectors';
import { storageService } from 'affiny-core/services/storage/storage';
import { websocket } from 'affiny-core/services/websockets';
import {
  all,
  call,
  cancel,
  put,
  select,
  spawn,
  take,
  takeLatest,
} from 'redux-saga/effects';
import { config } from '../../env';
import { parseHostUrl } from '../../services/config/parser';
import {
  EWebsocketActionType,
  TWebsocketAction,
} from '../../services/websockets/actions';
import { SocketNotificationEventType } from '../../services/websockets/types';
import { EBrand } from '../../types/env.types';
import { AccountActions } from '../account/actions';
import { refreshAccountSaga } from '../account/saga';
import { login } from '../api';
import { LogoutResource } from '../api/core/resources/logout.resource';
import { getRefreshToken, refreshAuthentication } from '../api/core/saga';
import { getConfigurationsSaga } from '../configurations/saga';
import { countersActions } from '../counters/actions';
import { ActionsOfType } from '../helpers';
import { checkHasCompletedProfile } from '../helpers/checkSaga';
import { getProfileMemberChoicesSaga } from '../memberchoices/saga';
import { messagesActions } from '../messages/actions';
import { getReflistsSaga } from '../reflists/saga';
import { getSubscriptionsAction } from '../subscriptions/actions';
import { authActions, EAuthActionType, TAuthAction } from './actions';
import { IToken, IWebsocketCredentials } from './types';

export const DEFAULTS = {
  DELAY: 1000,
  MAX_ATTEMPTS: 5,
  MAX_WAIT_TIME: 5000,
};

export function* loginSaga(
  action: ActionsOfType<TAuthAction, EAuthActionType.LOGIN>
) {
  const {
    username,
    password,
    captcha,
    blackbox,
    onSubmitError,
  } = action.payload;

  try {
    const token: IToken = yield call(
      login,
      username,
      password,
      captcha,
      blackbox
    );

    yield put(authActions.loginSuccess(token));
    yield spawn(reauthenticatePeriodically);
    yield call(fetchDataAndRedirect);
  } catch (error) {
    if (onSubmitError) {
      yield call(onSubmitError);
    }

    yield put(authActions.authDataCleanup());
    throw error;
  }
}

export function* storeWebsocketNotification(action: TWebsocketAction) {
  if (!(yield* checkHasCompletedProfile())) return;

  switch (action.type) {
    case EWebsocketActionType.COUNTER_RECEIVED: {
      yield put(countersActions.updateCounterFromWebsocket(action.payload));
      break;
    }
    case EWebsocketActionType.EVENT_RECEIVED:
      switch (action.payload.type) {
        case SocketNotificationEventType.NEW_MESSAGE_RECEIVED:
          yield put(messagesActions.newMessageFromWebsocket(action.payload));
      }
      break;
  }
}

export function* reauthenticatePeriodically() {
  try {
    while (true) {
      yield call(handlePeriodicalAuth);
    }
  } catch (error) {
    yield cancel();
  }
}

export function* handlePeriodicalAuth() {
  const refreshToken: string | undefined = yield* getRefreshToken();

  if (!refreshToken) throw Error(NO_REFRESH_TOKEN);

  const accessToken: string | undefined = yield select(selectAccessToken);

  if (!accessToken || isNearlyExpired(accessToken)) {
    yield call(refreshAuthentication);
  }

  yield call(delay, reauthDelay);
}

export function* handleWebSocketReconnection(
  pastNumberOfRetries = 0,
  noMaxRetry = false
) {
  if (!(yield* checkHasCompletedProfile())) return;

  let wait = DEFAULTS.DELAY;
  let numberOfRetries = pastNumberOfRetries;

  if (!noMaxRetry) {
    numberOfRetries = pastNumberOfRetries + 1;

    if (numberOfRetries > DEFAULTS.MAX_ATTEMPTS) return;

    wait = Math.min(numberOfRetries * DEFAULTS.DELAY, DEFAULTS.MAX_WAIT_TIME);
  }

  yield call(delay, wait);
  yield call(connectToWebSocket, numberOfRetries);
}

export function* connectToWebSocket(numberOfRetries: number): any {
  if (!(yield* checkHasCompletedProfile())) return;

  const remEnc = yield call([storageService, 'getRefreshToken']);
  const websocketCredentials: IWebsocketCredentials = {
    clientId: config.env.meeticApi.xClientId,
    remEnc,
  };

  yield call([websocket, 'connect']);
  const socket = yield call([websocket, 'subscribe'], websocketCredentials);

  try {
    while (true) {
      const webSocketAction = yield take(socket);
      const {
        type,
        payload: { noMaxRetry },
      } = webSocketAction;

      if (type === EWebsocketActionType.RECONNECT) {
        yield call([socket, 'close']);
        yield call(handleWebSocketReconnection, numberOfRetries, noMaxRetry);
        break;
      }

      yield call(storeWebsocketNotification, webSocketAction);
    }
  } catch (e) {
    socket.close();
  }
}

export function* fetchDataAndRedirect() {
  yield call(fetchData);
  yield call(fastForwardSaga);
}

export function* fetchData() {
  yield call(refreshAccountSaga);
  yield call(getProfileMemberChoicesSaga);
  // Needed calls triggered simultaneously
  yield all([
    // TODO MOVE THIS OUTSIDE FETCH DATA
    spawn(handleWebSocketReconnection),
    put(AccountActions.getPicturesAccount()),
    put(AccountActions.getMyProfile()),
    isWeb() && call(getConfigurationsSaga),
    put(getSubscriptionsAction()),
    // TODO perform these calls only when needed but not in fastForwardSaga
    call(getReflistsSaga),
  ]);
}

export function* fastForwardSaga() {
  const refreshToken = yield* getRefreshToken();

  if (!refreshToken) {
    return yield put(
      authActions.fastForwardNavigate({
        route: { goLogin: true },
      })
    );
  }

  const { fastForward } = config.env;

  if (fastForward?.disabled) {
    const { chapterLabel, questionIndex, pageAction } = fastForward;

    if (pageAction) {
      return yield put(
        authActions.fastForwardNavigate({
          route: { [pageAction]: true },
        })
      );
    }

    if (chapterLabel) {
      return yield put(
        authActions.fastForwardNavigate({
          route: { goQuestion: true },
          params: {
            chapterLabel,
            questionIndex: questionIndex || 0,
          },
        })
      );
    }

    return yield put(
      authActions.fastForwardNavigate({
        route: { startProfileCapture: true },
      })
    );
  }

  // TODO: rajouter un check pour savoir si on est loggé ou non sur le refreshToken
  const hasProfileComplete = yield select(
    selectAccountAttributeElement,
    'hasCompletedProfile'
  );

  if (hasProfileComplete) {
    return yield put(
      authActions.fastForwardNavigate({ route: { goHome: true } })
    );
  }

  const firstUnanswered = yield select(getFirstUnansweredQuestion);

  if (firstUnanswered) {
    const {
      questionIndex,
      chapterLabel,
      previousChapterLabel,
    } = firstUnanswered;

    if (questionIndex === 0) {
      if (previousChapterLabel) {
        return yield put(
          authActions.fastForwardNavigate({
            route: { goToNextChapter: true },
            params: { chapterLabel },
          })
        );
      } else {
        return yield put(
          authActions.fastForwardNavigate({
            route: { startProfileCapture: true },
          })
        );
      }
    } else {
      return yield put(
        authActions.fastForwardNavigate({
          route: { goQuestion: true },
          params: { chapterLabel, questionIndex },
        })
      );
    }
  } else {
    const hasPictures = yield select(hasPicturesFilled);
    const hasAnnounce = yield select(selectAccountAnnounce);
    const isXsell = yield select(selectAccountAttributeElement, 'isCrossSell');

    if (
      isWeb() &&
      hasAnnounce &&
      isXsell &&
      // Exclude Partner brand because consentement is already asked and cross-sell page is displayed in dating.
      parseHostUrl(window.location.hostname).brand !== EBrand.PA
    ) {
      return yield put(
        authActions.fastForwardNavigate({
          route: { goXsell: true },
        })
      );
    } else if (hasPictures) {
      return yield put(
        authActions.fastForwardNavigate({
          route: { goAnnounce: true },
        })
      );
    } else {
      return yield put(
        authActions.fastForwardNavigate({
          route: { goUploadPicture: true },
        })
      );
    }
  }
}

// TODO: Change this function so that it will be used in both app and web
// for now it is only used in app
export function* redirectIfAuthenticated() {
  yield call(refreshAuthentication);
  yield call(fetchDataAndRedirect);
}

export function* authDataCleanupSaga() {
  try {
    yield call([storageService, 'forgetRefreshToken']);
    yield call(DangerousStore.purge);
    yield put(authActions.resetStore());
  } catch (e) {
    // DO NOTHING
  }
}

export function* logoutSaga() {
  const accessToken = yield select(selectAccessToken);

  try {
    yield call([websocket, 'close']);

    if (accessToken) {
      yield call(LogoutResource.logout);
    }
  } catch (e) {
    // DO NOTHING
  } finally {
    yield put(authActions.authDataCleanup());
    yield put(
      authActions.fastForwardNavigate({
        route: { goWelcome: true },
      })
    );
  }
}

export function* authRootSaga() {
  yield all([
    takeLatest(
      EAuthActionType.LOGIN,
      handleAsyncStatus(loginSaga, EAsyncEventTypes.LOGIN)
    ),
    takeLatest(EAuthActionType.AUTH_DATA_CLEANUP, authDataCleanupSaga),
    takeLatest(EAuthActionType.LOGOUT, logoutSaga),
  ]);
}
